import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import AsanaConnectForm from "./AsanaConnectForm";

export const ASANA_CONSOLE_URL = "https://app.asana.com/";
export const ASANA_TOKEN_URL = "https://app.asana.com/0/my-apps";

const AsanaIntegration = () => {
  const colors = useColors();
  const [guideMode] = useState(false);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("asana");
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { registerAsanaIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerAsanaIntegration(values.token)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Asana integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated
            ? "Successfully integrated with Asana"
            : "Integrate with Asana"
        }
        image={logos.AsanaLogo}
        name={"Asana"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <>
              {/* Config page will be implemented in future */}
              <Typography variant="body1">
                Configuration options will be available in a future update.
              </Typography>
            </>
          ) : (
            <>
              {guideMode && (
                <Box sx={{ mb: 2 }}>
                  <IntegrationGuideAlert />
                </Box>
              )}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Asana">
                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(ASANA_CONSOLE_URL, "_blank");
                      }}
                      disabled={guideMode}
                    >
                      Login to Asana Account
                    </PrimaryButton>
                  </Box>
                  <Typography
                    variant={"body2"}
                    sx={{ mt: 1, color: colors.text_secondary }}
                  >
                    Important: Tasks and tickets created via this integration
                    will be attributed to the Asana user associated with this
                    Personal Access Token.
                    <br />
                    Please ensure you authenticate with the appropriate
                    organizational account for proper task attribution.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Obtain a Personal Access Token">
                  <Typography variant={"body2"}>
                    Create a Personal Access Token to allow the platform to
                    access Asana.
                  </Typography>

                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(ASANA_TOKEN_URL, "_blank");
                      }}
                      disabled={guideMode}
                    >
                      Create a Personal Access Token
                    </PrimaryButton>
                  </Box>
                  <Typography variant={"body2"}>
                    Click <strong>Create new token</strong>
                  </Typography>
                  <Typography variant={"body2"}>
                    Give it a meaningful name e.g{" "}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      component="span"
                    >
                      <strong>{"Breeze-Asana"}</strong>
                      <CustomCopyButton
                        copyTitle="token name"
                        copyText="Breeze-Asana"
                      />
                    </Box>
                  </Typography>

                  <GuidelineInstruction text="" />
                </GuidelineStep>
                <GuidelineStep title="Connect with Personal Access Token">
                  <Typography variant={"body2"}>
                    Enter your Personal Access Token to connect
                  </Typography>
                  <AsanaConnectForm onSubmit={onSubmit} guideMode={guideMode} />
                </GuidelineStep>
              </GuidelineStepByStep>
            </>
          )
        }
      />
    </Box>
  );
};

export default AsanaIntegration;
